import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Item, PaneType, Section} from '../catalog-manager.models';
import {getImageUrl} from '../catalog-manager.utils';
import {AdminService} from "../../../../services/AdminService";
import {NavController} from "@ionic/angular";

@Component({
    selector: 'app-item-list',
    templateUrl: './item-list.component.html',
    styleUrls: ['./item-list.component.scss']
})
export class ItemListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() items: Item[] = [];
    @Input() markedItemIds: Set<number> = new Set();
    @Input() isActive: boolean = false;
    @Input() targetSectionId: number | null = null;
    @Input() navigationVersion: number = 0;
    @Input() pane: PaneType = 'left';
    @Input() globalSearchMode: boolean = false;
    @Input() sections: Section[] = [];

    @Output() itemMarked = new EventEmitter<number>();
    @Output() itemSelected = new EventEmitter<Item>();
    @Output() itemRenamed = new EventEmitter<{ id: number; name: string }>();
    @Output() itemsDropped = new EventEmitter<{ itemIds: number[]; toSectionId: number | null }>();
    @Output() itemsCopied = new EventEmitter<{ itemIds: number[]; toSectionId: number | null }>();

    editingItem: number | null = null;
    editingName: string = '';
    lastSelectedIndex: number = -1;
    failedImages = new Set<number>();
    draggedItems: number[] = [];
    isDragOver: boolean = false;
    
    // For drag-to-select functionality
    @ViewChild('itemGridContainer', { static: false }) itemGridContainer?: ElementRef<HTMLElement>;
    isSelecting: boolean = false;
    selectionStart: { x: number; y: number } | null = null;
    selectionEnd: { x: number; y: number } | null = null;
    selectionRect: { left: number; top: number; width: number; height: number } | null = null;
    private globalMouseUpListener?: (event: MouseEvent) => void;
    private globalMouseMoveListener?: (event: MouseEvent) => void;
    
    // Track previous state for change detection
    private previousItemsLength: number = 0;
    private previousTargetSectionId: number | null = null;
    private previousNavigationVersion: number = 0;

    // For move/copy dialog
    showMoveDialog = false;
    pendingDraggedItemIds: number[] = [];

    // For search functionality
    searchQuery: string = '';

    // For pagination
    currentPage: number = 1;
    itemsPerPage: number = 18;

    // Cached values for performance (public for template binding)
    filteredItems: Item[] = [];
    paginatedItems: Item[] = [];
    totalPages: number = 0;
    startIndex = 0;

    // Performance optimization: Map for O(1) section lookup
    private sectionLookupMap: Map<number, string> = new Map();

    constructor(private adminSerivce:AdminService, public nav:NavController) {
    }


    getImageUrl = getImageUrl;

    ngOnInit() {
        // Initialize tracking variables
        this.previousItemsLength = this.items.length;
        this.previousTargetSectionId = this.targetSectionId;
        this.previousNavigationVersion = this.navigationVersion;
        // Initialize cache on component load
        this.updateCache();
    }

    ngOnDestroy() {
        // Clean up global event listeners
        this.removeGlobalEventListeners();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Update section lookup map when sections change
        if (changes['sections']) {
            this.sectionLookupMap.clear();
            this.sections.forEach(section => {
                this.sectionLookupMap.set(section.id, section.name);
            });
        }

        // Check if any of the inputs that should trigger a page reset have changed
        const shouldReset = 
            changes['items'] || 
            changes['targetSectionId'] || 
            changes['navigationVersion'];
        
        if (shouldReset) {
            this.currentPage = 1;
            this.previousItemsLength = this.items.length;
            this.previousTargetSectionId = this.targetSectionId;
            this.previousNavigationVersion = this.navigationVersion;
            this.updateCache();
        }
    }

    private updateCache() {
        // Update filtered items
        if (!this.searchQuery.trim()) {
            this.filteredItems = this.items;
        } else {
            const query = this.searchQuery.toLowerCase().trim();
            this.filteredItems = this.items.filter(item =>
                item.name?.toLowerCase().includes(query) ||
                item.code?.toLowerCase().includes(query)
            );
        }

        // Update total pages
        this.totalPages = Math.ceil(this.filteredItems.length / this.itemsPerPage);

        // Ensure current page is within bounds
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = this.totalPages;
        }

        // Update paginated items
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedItems = this.filteredItems.slice(startIndex, endIndex);
    }

    onImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        const urlMatch = img.src.match(/images\/(\d+)_main/);
        if (urlMatch) {
            const id = parseInt(urlMatch[1], 10);
            this.failedImages.add(id);
        }
        img.style.display = 'none';
    }

    hasValidImage(item: Item): boolean {
        return !this.failedImages.has(item.id);
    }

    isMarked(itemId: number): boolean {
        return this.markedItemIds.has(itemId);
    }

    onItemClick(event: MouseEvent, item: Item, index: number) {
        // Single click to mark
        this.itemMarked.emit(item.id);
        this.lastSelectedIndex = index;
    }

    onItemDoubleClick(event: MouseEvent, item: Item) {
        // Double click to open details
        event.preventDefault();
        this.itemSelected.emit(item);
    }

    startEdit(event: Event, item: Item) {
        event.stopPropagation();
        this.editingItem = item.id;
        this.editingName = item.name;
    }

    saveEdit(itemId: number) {
        if (this.editingName.trim()) {
            this.itemRenamed.emit({id: itemId, name: this.editingName.trim()});
        }
        this.editingItem = null;
    }

    cancelEdit() {
        this.editingItem = null;
    }

    onDragStart(event: DragEvent, item: Item) {
        // If item is marked, drag all marked items, otherwise just this item
        if (this.isMarked(item.id)) {
            this.draggedItems = Array.from(this.markedItemIds);
        } else {
            this.draggedItems = [item.id];
        }

        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'copyMove';
            event.dataTransfer.setData('application/json', JSON.stringify(this.draggedItems));
            event.dataTransfer.setData('text/plain', JSON.stringify(this.draggedItems));
        }
    }

    private isRootLevel(): boolean {
        return this.targetSectionId === null;
    }

    onDragOver(event: DragEvent) {
        // Only allow drop if not at root level
        if (this.isRootLevel()) {
            return;
        }

        event.preventDefault();
        this.isDragOver = true;
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    }

    onDragLeave(event: DragEvent) {
        // Only set isDragOver to false if leaving the viewport itself, not a child element
        const target = event.currentTarget as HTMLElement;
        const relatedTarget = event.relatedTarget as HTMLElement;
        if (!target.contains(relatedTarget)) {
            this.isDragOver = false;
        }
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        // Don't allow drop at root level
        if (this.isRootLevel()) {
            alert('Please navigate into a section before dropping items');
            this.draggedItems = [];
            this.pendingDraggedItemIds = [];
            return;
        }

        try {
            const dataJson = event.dataTransfer?.getData('application/json') || event.dataTransfer?.getData('text/plain');
            if (dataJson) {
                const draggedItemIds = JSON.parse(dataJson) as number[];

                // Store the dragged items and show dialog
                this.pendingDraggedItemIds = draggedItemIds;
                this.showMoveDialog = true;
            }
        } catch (e) {
            console.error('Error parsing drag data:', e);
        }

        this.draggedItems = [];
    }

    onDialogAction(action: 'move' | 'copy' | 'cancel') {
        console.log('Dialog action:', action, 'itemIds:', this.pendingDraggedItemIds, 'targetSectionId:', this.targetSectionId, 'pane:', this.pane);
        this.showMoveDialog = false;

        if (action === 'move') {
            // Move items - emit drop event
            console.log('Emitting itemsDropped event');
            this.itemsDropped.emit({
                itemIds: this.pendingDraggedItemIds,
                toSectionId: this.targetSectionId
            });
        } else if (action === 'copy') {
            // Copy items
            console.log('Emitting itemsCopied event');
            this.itemsCopied.emit({
                itemIds: this.pendingDraggedItemIds,
                toSectionId: this.targetSectionId
            });
        }
        // Cancel does nothing

        this.pendingDraggedItemIds = [];
    }

    trackBy(index: number, item: Item): number {
        return item.id;
    }

    getSectionNamesForItem(item: Item): string {
        if (!this.globalSearchMode || !item.section_ids || item.section_ids.length === 0) {
            return '';
        }
        const sectionNames = item.section_ids
            .map(sectionId => this.sectionLookupMap.get(sectionId) || '')
            .filter(name => name !== '');
        return sectionNames.join(', ');
    }

    onSearchChange() {
        this.currentPage = 1;
        this.updateCache();
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.updateCache();
        }
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.updateCache();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.updateCache();
        }
    }

    clearSearch() {
        this.searchQuery = '';
        this.currentPage = 1;
        this.updateCache();
    }

    // Drag-to-select functionality
    onMouseDown(event: MouseEvent) {
        // Only start selection on left click and not on item cards
        if (event.button !== 0 || (event.target as HTMLElement).closest('.item-card')) {
            return;
        }

        const container = event.currentTarget as HTMLElement;
        const rect = container.getBoundingClientRect();
        
        this.isSelecting = true;
        this.selectionStart = {
            x: event.clientX - rect.left + container.scrollLeft,
            y: event.clientY - rect.top + container.scrollTop
        };
        this.selectionEnd = { ...this.selectionStart };
        this.updateSelectionRect();

        // Add global listeners to handle mouse events outside the container
        this.addGlobalEventListeners();

        event.preventDefault();
    }

    onMouseMove(event: MouseEvent) {
        if (!this.isSelecting || !this.selectionStart) {
            return;
        }

        const container = event.currentTarget as HTMLElement;
        const rect = container.getBoundingClientRect();
        
        this.selectionEnd = {
            x: event.clientX - rect.left + container.scrollLeft,
            y: event.clientY - rect.top + container.scrollTop
        };
        
        this.updateSelectionRect();
        this.selectItemsInRect();
    }

    onMouseUp(event: MouseEvent) {
        if (!this.isSelecting) {
            return;
        }

        this.endSelection();
    }

    private addGlobalEventListeners() {
        this.globalMouseMoveListener = (event: MouseEvent) => {
            if (!this.isSelecting || !this.selectionStart || !this.itemGridContainer) {
                return;
            }

            const container = this.itemGridContainer.nativeElement;
            const rect = container.getBoundingClientRect();
            
            this.selectionEnd = {
                x: event.clientX - rect.left + container.scrollLeft,
                y: event.clientY - rect.top + container.scrollTop
            };
            
            this.updateSelectionRect();
            this.selectItemsInRect();
        };

        this.globalMouseUpListener = (event: MouseEvent) => {
            if (this.isSelecting) {
                this.endSelection();
            }
        };

        document.addEventListener('mousemove', this.globalMouseMoveListener);
        document.addEventListener('mouseup', this.globalMouseUpListener);
    }

    private removeGlobalEventListeners() {
        if (this.globalMouseMoveListener) {
            document.removeEventListener('mousemove', this.globalMouseMoveListener);
            this.globalMouseMoveListener = undefined;
        }
        if (this.globalMouseUpListener) {
            document.removeEventListener('mouseup', this.globalMouseUpListener);
            this.globalMouseUpListener = undefined;
        }
    }

    private endSelection() {
        this.isSelecting = false;
        this.selectionStart = null;
        this.selectionEnd = null;
        this.selectionRect = null;
        this.removeGlobalEventListeners();
    }

    private updateSelectionRect() {
        if (!this.selectionStart || !this.selectionEnd) {
            return;
        }

        const left = Math.min(this.selectionStart.x, this.selectionEnd.x);
        const top = Math.min(this.selectionStart.y, this.selectionEnd.y);
        const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
        const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);

        this.selectionRect = { left, top, width, height };
    }

    private selectItemsInRect() {
        if (!this.selectionRect || !this.itemGridContainer) {
            return;
        }

        const container = this.itemGridContainer.nativeElement;
        const itemCards = container.querySelectorAll('.item-card');
        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const scrollLeft = container.scrollLeft;

        itemCards.forEach((card, index) => {
            const cardElement = card as HTMLElement;
            const cardRect = cardElement.getBoundingClientRect();
            
            // Calculate card position relative to container with scroll offset
            const cardLeft = cardRect.left - containerRect.left + scrollLeft;
            const cardTop = cardRect.top - containerRect.top + scrollTop;
            const cardRight = cardLeft + cardRect.width;
            const cardBottom = cardTop + cardRect.height;

            // Check intersection with selection rectangle
            const selRect = this.selectionRect!;
            const isIntersecting = !(
                cardRight < selRect.left ||
                cardLeft > selRect.left + selRect.width ||
                cardBottom < selRect.top ||
                cardTop > selRect.top + selRect.height
            );

            if (isIntersecting) {
                // Get item from filteredItems based on the index of the card in the visible list
                // Use startIndex since template uses filteredItems.slice(startIndex, startIndex+itemsPerPage)
                const itemIndex = this.startIndex + index;
                if (itemIndex >= 0 && itemIndex < this.filteredItems.length) {
                    const item = this.filteredItems[itemIndex];
                    if (!this.isMarked(item.id)) {
                        this.itemMarked.emit(item.id);
                    }
                }
            }
        });
    }
}
